import React, { useState } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fab, FormControlLabel, TextField, Tooltip, Typography } from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import { getToken } from "../services/auth";

export default function AnnouncementWidget({ onPublished }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [body, setBody] = useState("");
  const [consent, setConsent] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const close = () => {
    if (publishing) return;
    setOpen(false); setConfirmOpen(false); setConsent(false); setError(""); setSuccess("");
  };

  const review = () => {
    if (!body.trim()) return;
    setConsent(false); setError(""); setConfirmOpen(true);
  };

  const publish = async () => {
    if (!consent) return;
    setPublishing(true); setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/announcements`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ body: body.trim(), consentAccepted: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Unable to publish announcement");
      setConfirmOpen(false); setSuccess(data.message); setBody(""); setConsent(false); onPublished?.();
      window.setTimeout(() => { setOpen(false); setSuccess(""); }, 1500);
    } catch (publishError) { setError(publishError.message); setConfirmOpen(false); }
    finally { setPublishing(false); }
  };

  return <>
    <Tooltip title="Publish announcement" placement="left">
      <Fab size="medium" aria-label="Publish announcement" onClick={() => setOpen(true)} sx={{ position: "fixed", right: 22, bottom: 80, zIndex: 1250, color: "white", bgcolor: "#6b5bc7", boxShadow: "0 10px 28px rgba(65,52,143,.28)", "&:hover": { bgcolor: "#5849b2" } }}><CampaignRoundedIcon fontSize="small" /></Fab>
    </Tooltip>
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Publish announcement</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>This announcement will appear in every active user’s announcement inbox and expire after 72 hours.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <TextField autoFocus multiline minRows={5} fullWidth label="Announcement" value={body} onChange={(event) => setBody(event.target.value.slice(0, 500))} inputProps={{ maxLength: 500 }} helperText={`${body.length}/500 characters`} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={close} disabled={publishing}>Cancel</Button><Button variant="contained" startIcon={<CampaignRoundedIcon />} onClick={review} disabled={!body.trim() || publishing}>Review announcement</Button></DialogActions>
    </Dialog>
    <Dialog open={confirmOpen} onClose={() => !publishing && setConfirmOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Confirm global announcement</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>This announcement will be sent to every active application user. It cannot be recalled, but it will expire automatically after 72 hours.</Alert>
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#f6f8fb", border: "1px solid #e2e9f0" }}><Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{body.trim()}</Typography></Box>
        <FormControlLabel control={<Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)} />} label="I understand that this announcement will be sent to all users." />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setConfirmOpen(false)} disabled={publishing}>Go back</Button><Button variant="contained" color="warning" onClick={publish} disabled={!consent || publishing}>{publishing ? <Box sx={{ display: "flex", gap: 1 }}><CircularProgress size={20} color="inherit" />Publishing</Box> : "Accept and publish"}</Button></DialogActions>
    </Dialog>
  </>;
}
